import { observer } from "mobx-react-lite";
import { PropsWithChildren, ReactNode, useContext } from "react";

import { MessageBar, MessageBarType, SideRailMenuItem } from "@bps/fluent-ui";
import { ClaimStatuses } from "@libs/gateways/acc/AccGateway.dtos.ts";
import { OnHoldMessageBar } from "@modules/acc/screens/claim/components/OnHoldMessageBar.tsx";
import { ClaimContext } from "@modules/acc/screens/claim/context/ClaimContext.ts";
import { ClaimDocumentViewerDialog } from "@modules/acc/screens/claims/components/ClaimDocumentViewerDialog.tsx";
import { useStores } from "@stores/hooks/useStores.ts";
import { CardFormLayout } from "@ui-components/card-form-layout/CardFormLayout.tsx";

import { AccidentAndEmploymentDetailsFormValidator } from "../validators/AccidentAndEmploymentDetailsFormValidator.tsx";
import { InjuryDiagnosisAndAssistanceFormValidator } from "../validators/InjuryDiagnosisAndAssistanceFormValidator.tsx";
import { PatientDeclarationFormValidator } from "../validators/PatientDeclarationFormValidator.tsx";
import { PersonalDetailsFormValidator } from "../validators/PersonalDetailsFormValidator.tsx";
import { ProviderDeclarationFormValidator } from "../validators/ProviderDeclarationFormValidator.tsx";
import { ReferralsOutFormValidator } from "../validators/ReferralsOutFormValidator.tsx";
import { ClaimEditFormBody } from "./ClaimEditFormBody.tsx";
import { ClaimFormSideRailLabels, ClaimsCardIds } from "./ClaimFormEnums.ts";
import { ClaimFormSideMenuText } from "./ClaimFormSideMenuText.tsx";
import { ClaimViewFormBody } from "./ClaimViewFormBody.tsx";

interface ClaimFormInnerProps extends PropsWithChildren {
  header: ReactNode;
}

export const ClaimFormInner = observer((props: ClaimFormInnerProps) => {
  const { core } = useStores();
  const { header, children } = props;

  const { claim, provider } = useContext(ClaimContext);

  const getMessageBar = () => {
    const messageBarComponents = [];

    if (claim.private) {
      messageBarComponents.push(
        <MessageBar
          key="private-warning"
          messageBarType={MessageBarType.warning}
        >
          This claim is no longer valid. This condition is being paid for
          privately.
        </MessageBar>
      );
    }

    if (claim.isOnHold) {
      messageBarComponents.push(<OnHoldMessageBar />);
    }

    return messageBarComponents.length > 0 ? (
      <>{messageBarComponents}</>
    ) : undefined;
  };

  const claimMenuItems: SideRailMenuItem[] = [
    {
      text: (
        <ClaimFormSideMenuText
          text={ClaimFormSideRailLabels.personalDetails}
          validator={new PersonalDetailsFormValidator()}
        />
      ),
      id: ClaimsCardIds.personalDetails
    },
    {
      text: (
        <ClaimFormSideMenuText
          text={ClaimFormSideRailLabels.accidentAndEmploymentDetails}
          validator={new AccidentAndEmploymentDetailsFormValidator()}
        />
      ),
      id: ClaimsCardIds.accidentAndEmploymentDetails
    },
    {
      text: (
        <ClaimFormSideMenuText
          text={ClaimFormSideRailLabels.patientDeclaration}
          validator={new PatientDeclarationFormValidator()}
        />
      ),
      id: ClaimsCardIds.patientDeclaration
    },
    {
      text: (
        <ClaimFormSideMenuText
          text={ClaimFormSideRailLabels.injuryDiagnosisAndAssistance}
          validator={new InjuryDiagnosisAndAssistanceFormValidator()}
        />
      ),
      id: ClaimsCardIds.injuryDiagnosisAndAssistance
    },
    {
      text: (
        <ClaimFormSideMenuText
          text={ClaimFormSideRailLabels.providerDeclaration}
          validator={
            new ProviderDeclarationFormValidator(core, provider?.contractTypes)
          }
        />
      ),
      id: ClaimsCardIds.providerDeclaration
    }
  ];

  if (
    !claim.isViewMode ||
    (claim.isViewMode && !!claim.referralDetails?.length)
  ) {
    claimMenuItems.push({
      text: (
        <ClaimFormSideMenuText
          text={ClaimFormSideRailLabels.referralsOut}
          validator={new ReferralsOutFormValidator()}
        />
      ),
      id: ClaimsCardIds.referralsOut
    });
  }

  return (
    <CardFormLayout
      sideRailMenuItems={claimMenuItems}
      header={header}
      messageBar={getMessageBar()}
    >
      {claim.isViewMode || claim.claimStatus === ClaimStatuses.Accredited ? (
        <ClaimViewFormBody />
      ) : (
        <ClaimEditFormBody />
      )}
      {children}
      <ClaimDocumentViewerDialog />
    </CardFormLayout>
  );
});
